/**
 * @fileoverview Printers API functions
 * 
 * Handles printer management including:
 * - Printer CRUD operations
 * - QZ Tray integration
 * - Direct printing
 * - Certificate management
 * 
 * @module api/printers
 */

import qz from 'qz-tray';
import apiClient, { 
  handleUnauthorizedResponse, 
  getAuthHeaders, 
  getRestaurantId 
} from './client';

// ============================================================================
// PRINTER CRUD
// ============================================================================

/**
 * Get all printers for the restaurant
 * @param {Object} router - Next.js router
 * @returns {Promise} Printers list
 */
export const getAllPrinters = async (router) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.get(`/admin/restaurant/${restaurantId}/printers/`, {
      headers: getAuthHeaders(),
    });
    return response;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Get a single printer by ID
 * @param {Object} router - Next.js router
 * @param {string} printerId - Printer ID
 * @returns {Promise} Printer details
 */
export const getPrinter = async (router, printerId) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.get(`/admin/restaurant/${restaurantId}/printers/${printerId}/`, {
      headers: getAuthHeaders(),
    });
    return response;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Add a new printer
 * @param {Object} router - Next.js router
 * @param {Object} data - Printer data
 * @returns {Promise} Created printer
 */
export const addPrinter = async (router, data) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.post(`/admin/restaurant/${restaurantId}/printers/`, data, {
      headers: getAuthHeaders(),
    });
    return response;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Update a printer
 * @param {Object} router - Next.js router
 * @param {string} printerId - Printer ID
 * @param {Object} data - Updated printer data
 * @returns {Promise} Updated printer
 */
export const updatePrinter = async (router, printerId, data) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.put(
      `/admin/restaurant/${restaurantId}/printers/${printerId}/`,
      data,
      { headers: getAuthHeaders() }
    );
    return response;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Delete a printer
 * @param {Object} router - Next.js router
 * @param {string} printerId - Printer ID
 * @returns {Promise} Deletion response
 */
export const deletePrinter = async (router, printerId) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.delete(`/admin/restaurant/${restaurantId}/printers/${printerId}/`, {
      headers: getAuthHeaders(),
    });
    return response;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Get all distinct printer locations
 * @param {Object} router - Next.js router
 * @returns {Promise} Locations array
 */
export const getPrinterLocations = async (router) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.get(`/admin/restaurant/${restaurantId}/printers/locations/`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

// ============================================================================
// DIRECT PRINTING
// ============================================================================

/**
 * Send print job to backend for direct printing
 * @param {Object} router - Next.js router
 * @param {Object} printJob - Print job data
 * @returns {Promise} Print response
 */
export const directPrint = async (router, printJob) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.post(`/print/direct/${restaurantId}`, printJob, {
      headers: getAuthHeaders(),
    });
    // Backends with the print queue answer a job id: track its real status
    // (queued / sent / printed / failed) on the order card.
    if (response.data?.job_id) {
      notifyPrintJob({
        job_id: response.data.job_id,
        status: response.data.job_status,
        order_id: printJob?.order_id ?? null,
        kind: printJob?.kind || (printJob?.principal ? 'receipt' : 'kitchen'),
        location: (printJob?.location || '').toLowerCase() || null,
        created_at: new Date().toISOString(),
      });
    }
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Update kitchen printed status for order menus
 * @param {Object} router - Next.js router
 * @param {string} orderId - Order ID
 * @param {Array} orderMenuIds - Array of order menu IDs
 * @returns {Promise} Update response
 */
export const updateKitchenPrinted = async (router, orderId, orderMenuIds) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.patch(
      `/admin/restaurant/${restaurantId}/order/${orderId}/kitchen-printed`,
      { order_menu_ids: orderMenuIds },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    console.error('Failed to update kitchen_printed:', error);
    throw error;
  }
};

/**
 * Update paid printed status for an order
 * @param {Object} router - Next.js router
 * @param {string} orderId - Order ID
 * @returns {Promise} Update response
 */
export const updatePaidPrinted = async (router, orderId) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.patch(
      `/admin/restaurant/${restaurantId}/order/${orderId}/paid-printed`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    console.error('Failed to update paid_printed:', error);
    throw error;
  }
};

// ============================================================================
// QZ TRAY INTEGRATION
// ============================================================================

/**
 * Set certificate for QZ Tray
 * @private
 */
const setCertif = async () => {
  
  qz.security.setCertificatePromise(function (resolve, reject) {
    
    apiClient
      .get('/print/digital-certificate', {
        responseType: 'text',
        headers: getAuthHeaders(),
      })
      .then((response) => {
        if (response.status === 200 && response.data) {
          resolve(response.data);
        } else {
          console.error('[setCertif] Invalid response:', response);
          reject('Failed to load the digital certificate.');
        }
      })
      .catch((err) => {
        console.error('[setCertif] Error fetching certificate:', err?.message || err, err?.response?.status, err?.response?.data);
        reject('Error loading the digital certificate: ' + (err?.message || err));
      });
  });
};

/**
 * Get QZ Tray configuration for printer
 * @private
 * @param {string} printerName - Printer name
 * @param {string} connectionType - Connection type (usb, bluetooth, wifi, ethernet)
 * @param {string} ipAddress - IP address for network printers (optional)
 * @param {number} port - Port for network printers (optional)
 * @returns {Object} QZ config
 */
const getConfig = (printerName, connectionType, ipAddress, port) => {
  const connType = (connectionType || 'usb').toLowerCase();
  
  // For network printers, use host:port format if IP is provided
  let printerTarget = printerName;
  if ((connType === 'wifi' || connType === 'network' || connType === 'ethernet') && ipAddress) {
    printerTarget = port ? `${ipAddress}:${port}` : ipAddress;
  }
  
  const config = qz.configs.create(printerTarget);
  
  switch (connType) {
    case 'usb':
      config.usingSecure = false;
      break;
    case 'bluetooth':
      config.usingSecure = true;
      break;
    case 'wifi':
    case 'network':
      config.usingSecure = true;
      break;
    case 'ethernet':
      config.usingSecure = false;
      break;
    default:
      // Default to USB for unknown types instead of throwing
      console.warn(`Unknown connection type: ${connectionType}, defaulting to USB`);
      config.usingSecure = false;
  }
  return config;
};

/**
 * Connect to QZ Tray with retry logic
 * @private
 * @param {number} retryCount - Number of retry attempts
 */
const connectToQZTray = async (retryCount = 3) => {
  if (!qz.websocket.isActive()) {
    for (let i = 0; i < retryCount; i++) {
      try {
        await qz.websocket.connect();
        break;
      } catch (error) {
        console.error('Error connecting to QZ Tray, attempt:', i + 1, error);
        if (i === retryCount - 1) {
          throw new Error('Unable to connect to QZ Tray after multiple attempts');
        }
      }
    }
  }
};

/**
 * Send data to QZ Tray for printing
 * @private
 * @param {string} message - Print message
 * @param {string} signature - Signature (optional)
 * @param {string} printerName - Printer name
 * @param {string} connectionType - Connection type
 * @param {string} ipAddress - IP address for network printers (optional)
 * @param {number} port - Port for network printers (optional)
 */
const sendToQZTray = async (message, signature, printerName, connectionType, ipAddress, port) => {
  
  await connectToQZTray();
  const config = getConfig(printerName, connectionType, ipAddress, port);
  
  if (!config) {
    throw new Error(`Printer configuration failed for: ${printerName} (${connectionType})`);
  }

  // Detect if the message contains ESC/POS commands
  const hasEscPosCommands = /[\x1B\x1D]/.test(message);
  
  let printData;
  if (hasEscPosCommands) {
    printData = [{
      type: 'raw',
      format: 'plain',
      data: message
    }];
  } else {
    printData = [{ 
      type: 'html', 
      format: 'plain', 
      data: message, 
      signature 
    }];
  }
  
  await qz.print(config, printData);
};

/**
 * Setup QZ Tray certificate and signature
 */
export const setupCertif = async () => {
  try {
    await setCertif();
    qz.security.setSignatureAlgorithm('SHA512');
    
    qz.security.setSignaturePromise(async function (toSign) {
      try {
        const response = await apiClient.post(
          '/print/ticket',
          { message: toSign },
          { headers: getAuthHeaders() }
        );
        const signature = response?.data;
        
        return new Promise(function (resolve, reject) {
          if (signature) {
            resolve(signature);
          } else {
            reject('Signature not available.');
          }
        });
      } catch (err) {
        console.error('Error signing data:', err);
        throw new Error('Error signing data.');
      }
    });
  } catch (error) {
    console.error('Error setting up certificate', error);
    throw error;
  }
};

/**
 * Print ticket via QZ Tray
 * @param {Object} router - Next.js router
 * @param {Object} printJob - Print job with message
 * @param {string} printerName - Printer name
 * @param {string} connectionType - Connection type (default: 'usb')
 * @param {string} ipAddress - IP address for network printers (optional)
 * @param {number} port - Port for network printers (optional)
 */
export const printTicket = async (router, printJob, printerName, connectionType = 'usb', ipAddress = null, port = null) => {
  await setupCertif();
  await sendToQZTray(printJob.message, null, printerName, connectionType, ipAddress, port);
};

// ============================================================================
// PRINT QUEUE (backend feat/print-queue)
// ============================================================================
// Every ticket is a job with a status. Older backends answer 404 on these routes:
// callers then keep the previous behaviour.

const printJobListeners = new Set();

/** Subscribe to print jobs created by directPrint (used by the print-job store). */
export const onPrintJob = (listener) => {
  printJobListeners.add(listener);
  return () => printJobListeners.delete(listener);
};

const notifyPrintJob = (job) => {
  printJobListeners.forEach((listener) => {
    try { listener(job); } catch { /* a listener must never break printing */ }
  });
};

/**
 * What the API does about printing: { jobs, server_auto_print, acknowledging_printer, max_age_minutes }.
 * Returns null when the backend has no print queue (404).
 */
export const getPrintCapabilities = async (router) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.get(`/print/capabilities/${restaurantId}`, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) handleUnauthorizedResponse(router);
    if (error.response?.status === 404) return null;
    throw error;
  }
};

/** Recent print jobs of the restaurant (or of one order). */
export const getPrintJobs = async (router, { orderId, minutes } = {}) => {
  const restaurantId = getRestaurantId();
  const params = {};
  if (orderId != null) params.order_id = orderId;
  if (minutes != null) params.minutes = minutes;
  try {
    const response = await apiClient.get(`/print/jobs/${restaurantId}`, { headers: getAuthHeaders(), params });
    return response.data?.jobs || [];
  } catch (error) {
    if (error.response?.status === 401) handleUnauthorizedResponse(router);
    if (error.response?.status === 404) return [];
    throw error;
  }
};

/** Send a failed / unacknowledged job to the printers again. */
export const retryPrintJob = async (router, jobId) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.post(`/print/jobs/${restaurantId}/${jobId}/retry`, null, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) handleUnauthorizedResponse(router);
    throw error;
  }
};

/**
 * Automatic kitchen tickets of an order, built by the API from the order itself
 * (idempotent: two tabs, or the tab and the API, never print the same lines twice).
 */
export const printOrderKitchen = async (router, orderId) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.post(`/print/orders/${restaurantId}/${orderId}/kitchen`, null, {
      headers: getAuthHeaders(),
    });
    return response.data?.jobs || [];
  } catch (error) {
    if (error.response?.status === 401) handleUnauthorizedResponse(router);
    throw error;
  }
};
